import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE, useAuth } from '../lib/auth';
import { AdminPageShell } from './AdminActivity';

type TeamAdmin = {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
  last_login_at: string | null;
  digest_enabled: boolean;
  is_active: boolean;
};

export default function AdminSettings() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [team, setTeam] = useState<TeamAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  async function loadTeam() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/team`, { credentials: 'include' });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { ok: boolean; admins: TeamAdmin[] };
      setTeam(data.admins ?? []);
      setError('');
    } catch (err) {
      console.error('team_fetch_failed', err);
      setError("Couldn't load the team.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  async function addAdmin(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/team`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail, name: newName }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNewEmail('');
      setNewName('');
      await loadTeam();
    } catch (err) {
      console.error('team_add_failed', err);
      setError("Couldn't add that admin. Check the email or duplicates.");
    } finally {
      setSaving(false);
    }
  }

  async function patchAdmin(id: string, patch: Partial<TeamAdmin>) {
    const previous = team;
    setTeam((list) => list.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('team_patch_failed', err);
      setTeam(previous);
      setError("Couldn't save that team change.");
    }
  }

  async function exportData() {
    try {
      const res = await fetch(`${API_BASE}/api/admin/export`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iswis-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export_failed', err);
      setError("Couldn't export data.");
    }
  }

  async function exportCsv() {
    try {
      const res = await fetch(`${API_BASE}/api/admin/export.csv`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iswis-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('csv_export_failed', err);
      setError("Couldn't export CSV.");
    }
  }

  return (
    <AdminPageShell active="Settings" onSignOut={signOut}>
      <main className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-5 sm:py-8">
        <div className="mb-5">
          <h1 className="font-bagel text-[30px] text-burgundy leading-none">Settings</h1>
          <p className="text-[13px] text-burgundy-faint mt-1">
            Team access, digest preferences, and export.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-danger/30 bg-danger/[0.06] px-4 py-3 text-danger text-[13px] font-semibold">
            {error}
          </div>
        )}

        <section className="bg-cream-bright border border-burgundy/[0.12] rounded-xl p-4 sm:p-5 mb-5">
          <h2 className="font-bagel text-[22px] text-burgundy mb-3">Team</h2>
          <form onSubmit={addAdmin} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mb-4">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="admin@example.com"
              className="min-h-11 rounded-lg border border-burgundy/[0.18] bg-cream px-3 text-[14px] focus:outline-none focus:border-pink"
              required
            />
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name, optional"
              className="min-h-11 rounded-lg border border-burgundy/[0.18] bg-cream px-3 text-[14px] focus:outline-none focus:border-pink"
            />
            <button
              type="submit"
              disabled={saving}
              className="min-h-11 rounded-lg bg-pink text-cream-bright px-4 text-[13px] font-bold disabled:opacity-50"
            >
              Add admin
            </button>
          </form>

          {loading ? (
            <div className="text-burgundy-faint text-sm">Loading team...</div>
          ) : (
            <div className="space-y-2">
              {team.map((member) => (
                <div
                  key={member.id}
                  className="rounded-xl border border-burgundy/[0.10] bg-cream-soft p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-burgundy text-[14px] truncate">
                      {member.name || member.email}
                      {member.id === admin?.id && (
                        <span className="ml-2 text-[11px] text-pink-deep">You</span>
                      )}
                    </div>
                    <div className="text-[12px] text-burgundy-faint truncate">{member.email}</div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-[12px] font-bold text-burgundy-soft">
                    Digest
                    <input
                      type="checkbox"
                      checked={member.digest_enabled}
                      onChange={(e) =>
                        patchAdmin(member.id, { digest_enabled: e.target.checked })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => patchAdmin(member.id, { is_active: !member.is_active })}
                    className={
                      'min-h-10 px-3 rounded-lg text-[12px] font-bold ' +
                      (member.is_active
                        ? 'border border-danger/30 text-danger'
                        : 'bg-teal text-cream-bright')
                    }
                  >
                    {member.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-cream-bright border border-burgundy/[0.12] rounded-xl p-4 sm:p-5">
          <h2 className="font-bagel text-[22px] text-burgundy mb-2">Data export</h2>
          <p className="text-[13px] text-burgundy-faint mb-4">
            Download submissions, notes, replies, activity, and admin records as JSON.
          </p>
          <button
            type="button"
            onClick={exportData}
            className="min-h-11 rounded-lg bg-burgundy text-cream-bright px-4 text-[13px] font-bold"
          >
            Export JSON
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className="ml-2 min-h-11 rounded-lg border border-burgundy/[0.18] text-burgundy px-4 text-[13px] font-bold"
          >
            Export CSV
          </button>
        </section>
      </main>
    </AdminPageShell>
  );
}
